import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Trophy,
	TrendingUp,
	Zap,
	Sparkles,
	ChevronDown,
	ChevronUp,
} from 'lucide-react'
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Skeleton } from '@/common/ui/skeleton'
import { Separator } from '@/common/ui/separator'
import useQueryGetTournamentPresets from '../../api/hooks/useQueryGetTournamentPresets'

const PRESET_ICONS: Record<string, React.ReactNode> = {
	MINI: <Zap className="h-5 w-5 text-yellow-500" />,
	SMALL: <Trophy className="h-5 w-5 text-blue-500" />,
	MEDIUM: <TrendingUp className="h-5 w-5 text-purple-500" />,
	BIG: <Sparkles className="h-5 w-5 text-orange-500" />,
}

const PRESET_LABELS: Record<string, string> = {
	MINI: 'ขนาดเล็ก',
	SMALL: 'ขนาดกลาง',
	MEDIUM: 'ขนาดใหญ่',
	BIG: 'ขนาดใหญ่พิเศษ',
}

interface PresetOption {
	preset: string
	name: string
	description: string
}

function PresetCard({ preset }: { preset: PresetOption }) {
	const navigate = useNavigate()
	const [showDescription, setShowDescription] = useState(false)

	const handleUsePreset = () => {
		navigate(`/operator/tournaments/create?preset=${preset.preset}`)
	}

	return (
		<Card className="flex flex-col transition-shadow hover:shadow-md">
			<CardHeader className="pb-3">
				<CardTitle className="flex items-center gap-2 text-base">
					{PRESET_ICONS[preset.preset] || <Trophy className="h-5 w-5" />}
					{preset.name}
				</CardTitle>
				<div className="flex flex-wrap gap-1.5">
					<Badge variant="outline" className="text-xs">
						{PRESET_LABELS[preset.preset] || preset.preset}
					</Badge>
				</div>
			</CardHeader>

			<CardContent className="flex-1 space-y-2 pb-3">
				<button
					type="button"
					onClick={() => setShowDescription((v) => !v)}
					className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
				>
					{showDescription ? (
						<ChevronUp className="h-3 w-3" />
					) : (
						<ChevronDown className="h-3 w-3" />
					)}
					{showDescription ? 'ซ่อนรายละเอียด' : 'ดูรายละเอียด'}
				</button>
				{showDescription && (
					<p className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
						{preset.description}
					</p>
				)}
			</CardContent>

			<CardFooter>
				<Button className="w-full" onClick={handleUsePreset}>
					<Sparkles className="mr-2 h-4 w-4" />
					ใช้เทมเพลตนี้
				</Button>
			</CardFooter>
		</Card>
	)
}

function PresetCardSkeleton() {
	return (
		<Card className="flex flex-col">
			<CardHeader className="pb-3">
				<Skeleton className="h-5 w-3/4" />
				<Skeleton className="h-5 w-16" />
			</CardHeader>
			<CardContent className="flex-1 pb-3">
				<Skeleton className="h-4 w-24" />
			</CardContent>
			<CardFooter>
				<Skeleton className="h-9 w-full" />
			</CardFooter>
		</Card>
	)
}

export default function TournamentPresetGallery() {
	const { data, isLoading } = useQueryGetTournamentPresets()
	const presets = data?.presets ?? []

	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-base font-semibold tracking-tight sm:text-lg">
					เทมเพลตทัวร์นาเมนต์
				</h3>
				<p className="mt-1 text-sm text-muted-foreground">
					เลือกเทมเพลตเพื่อสร้างทัวร์นาเมนต์สำเร็จรูป
					สามารถแก้ไขรายละเอียดและค่าต่างๆ ได้หลังจากสร้าง
				</p>
			</div>
			<Separator />

			{isLoading ? (
				<div className="grid grid-cols-4 gap-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<PresetCardSkeleton key={i} />
					))}
				</div>
			) : presets.length === 0 ? (
				<div className="flex h-32 items-center justify-center">
					<p className="text-muted-foreground">ไม่พบเทมเพลต</p>
				</div>
			) : (
				<div className="grid grid-cols-4 gap-4">
					{presets.map((preset: PresetOption) => (
						<PresetCard key={preset.preset} preset={preset} />
					))}
				</div>
			)}
		</div>
	)
}
